/**
 * 🌍 Sprint 3: Centralized UI Messages for i18n
 * 
 * All user-facing strings in one place for:
 * - Easier maintenance
 * - Future localization support
 * - Consistent brand voice
 */

export const ERROR_MESSAGES = {
  boundary: {
    title: 'Ops! Tivemos um tropeço na jornada',
    description: 'Algo inesperado aconteceu, mas não se preocupe. Vamos tentar esse passo novamente juntos?',
    persistentHelp: 'Se o problema persistir, estamos aqui para ajudar.',
    retryButton: 'tentar novamente',
    homeButton: 'voltar ao início',
    technicalDetails: 'Detalhes técnicos',
  },
  network: {
    offline: 'Parece que você está sem conexão. Verifique sua internet.',
    timeout: 'A conexão demorou mais que o esperado. Tente novamente.',
    serverError: 'Nossos servidores estão ocupados. Tente novamente em alguns instantes.',
  },
  auth: {
    sessionExpired: 'Sua sessão expirou. Faça login novamente para continuar.',
    unauthorized: 'Você não tem permissão para acessar essa área.',
    loginFailed: 'Ops, não conseguimos te identificar. Tente novamente.',
    accessDenied: 'Você não tem permissão para visualizar este conteúdo.',
  },
  // === Booking & Scheduling ===
  booking: {
    createFailed: 'Ops, não conseguimos agendar. Tente novamente!',
    meetingTokenFailed: 'Não conseguimos preparar a sala. Tente novamente.',
    meetingCreateFailed: 'Ops, a sala não pôde ser criada agora.',
    calendarEventFailed: 'Não foi possível criar o evento no calendário.',
    calendarConnectFailed: 'Não conseguimos conectar ao Google Calendar.',
    calendarBusyFailed: 'Não foi possível verificar a agenda.',
    calendarDisconnectFailed: 'Não foi possível desconectar o Google Calendar.',
  },
  // === Profile & Data ===
  profile: {
    loadFailed: 'Ops, seus dados não carregaram. Tente novamente.',
    updateFailed: 'Não conseguimos salvar suas alterações. Tente de novo!',
    createFailed: 'Não foi possível criar seu perfil agora.',
    teacherProfileRequired: 'Você precisa ter um perfil de educador.',
    fiscalSaveFailed: 'Ops, os dados fiscais não foram salvos.',
  },
  // === Student & Credentials ===
  student: {
    credentialsUpdateFailed: 'Ops, as credenciais não foram salvas.',
    accessUpdateFailed: 'Não conseguimos atualizar as permissões.',
    avatarUpdateFailed: 'Ops, o avatar não foi alterado.',
    notFound: 'Estudante não encontrado.',
    dataLoadFailed: 'Não foi possível carregar os dados do estudante.',
  },
  // === Creations & Gallery ===
  creations: {
    saveFailed: 'Ops, sua criação não foi salva. Tente novamente!',
    updateFailed: 'Não conseguimos atualizar a criação.',
    deleteFailed: 'Ops, não foi possível remover a criação.',
    commentFailed: 'Não conseguimos salvar o comentário.',
    commentPermission: 'Verifique se você tem permissão para comentar.',
  },
  // === Community & Groups ===
  community: {
    joinFailed: 'Ops, não conseguimos te inscrever. Tente novamente!',
    leaveFailed: 'Não foi possível cancelar sua inscrição.',
    groupJoinFailed: 'Ops, não foi possível entrar no grupo.',
    groupCreateFailed: 'Não conseguimos criar o grupo agora.',
  },
  // === Favorites ===
  favorites: {
    toggleFailed: 'Não foi possível atualizar seus favoritos.',
    loadFailed: 'Ops, suas favoritas não carregaram.',
  },
  // === XP & Progress ===
  progress: {
    xpAddFailed: 'Ops, o XP não foi registrado.',
    saveFailed: 'Não foi possível salvar as observações.',
    updateFailed: 'Ops, as observações não foram atualizadas.',
    journalSaveFailed: 'Não foi possível salvar sua reflexão. Tente novamente.',
    challengeSubmitFailed: 'Ops, o desafio não foi enviado.',
    pathEnrollFailed: 'Não conseguimos te matricular na trilha.',
  },
  // === Moderation & Admin ===
  admin: {
    moderationFailed: 'Ops, a moderação não foi processada.',
    impersonationFailed: 'Não foi possível iniciar a visualização.',
    applicationSubmitFailed: 'Ops, a candidatura não foi enviada. Tente novamente.',
    applicationUpdateFailed: 'Não conseguimos atualizar a candidatura.',
    insightsFailed: 'Ops, os insights não foram gerados.',
    competitorMapFailed: 'Não conseguimos mapear os concorrentes.',
    competitorExtractFailed: 'Ops, a extração não funcionou.',
    pdfGenerateFailed: 'Não foi possível gerar o PDF.',
    testRoomFailed: 'Ops, a sala de teste não foi criada.',
    curationFailed: 'Ops, a curadoria não foi processada.',
    auditFailed: 'Ops, a auditoria falhou.',
    imageUpdateFailed: 'Não foi possível atualizar a imagem.',
    imageSuggestFailed: 'Ops, não conseguimos sugerir uma imagem.',
    imageBanFailed: 'Ops, não foi possível banir a imagem.',
    reprocessFailed: 'Ops, o reprocessamento falhou.',
  },
  // === Misc ===
  misc: {
    copyFailed: 'Ops, o link não foi copiado.',
    cepNotFound: 'CEP não encontrado. Preencha manualmente.',
    genericLoad: 'Ops, não conseguimos carregar seus dados.',
    genericSave: 'Ops, não conseguimos salvar as alterações.',
    genericDescription: 'Tente novamente em alguns instantes.',
  },
} as const;

export const SUCCESS_MESSAGES = {
  save: 'Alterações salvas com sucesso!',
  delete: 'Removido com sucesso!',
  create: 'Criado com sucesso!',
  update: 'Atualizado com sucesso!',
} as const;

export const EMPTY_STATES = {
  classes: {
    title: 'Nenhum encontro encontrado',
    description: 'Que tal explorar novas experiências de aprendizado?',
  },
  teachers: {
    title: 'Nenhum educador encontrado',
    description: 'Tente ajustar seus filtros de busca.',
  },
  notifications: {
    title: 'Tudo em dia!',
    description: 'Você não tem novas notificações.',
  },
} as const;

export const SUPPORT_INFO = {
  message: 'Precisa de ajuda? Fale com a gente:',
  email: 'suporte@auli.live',
  mailto: 'mailto:suporte@auli.live',
} as const;

export const VIDEO_MESSAGES = {
  errors: {
    network: {
      title: 'Sem conexão com a internet',
      description: 'Verifique sua conexão e tente novamente.',
      icon: 'WifiOff',
    },
    api: {
      title: 'Servidor temporariamente indisponível',
      description: 'Nossa infraestrutura de vídeo está se recuperando. Tente novamente em alguns segundos.',
      icon: 'CloudOff',
    },
    permission: {
      title: 'Acesso não autorizado',
      description: 'Você não tem permissão para acessar este encontro. Verifique se está logado corretamente.',
      icon: 'ShieldX',
    },
    token: {
      title: 'Link expirado',
      description: 'O link deste encontro expirou. Volte ao seu painel e acesse novamente.',
      icon: 'Clock',
    },
    expired: {
      title: 'Esta aula já terminou',
      description: 'O período de acesso a este encontro encerrou. Volte ao seu painel para ver seus próximos encontros.',
      icon: 'Clock',
    },
    unknown: {
      title: 'Algo deu errado',
      description: 'Não conseguimos identificar o problema. Tente novamente ou entre em contato.',
      icon: 'AlertTriangle',
    },
  },
  retry: {
    button: 'Tentar novamente',
    attempting: 'Reconectando...',
    attempt: 'Tentativa {current} de {max}',
    maxReached: 'Número máximo de tentativas atingido',
  },
  support: {
    message: 'Se o problema persistir, entre em contato:',
    email: 'suporte@auli.live',
  },
} as const;

export const ONBOARDING_MESSAGES = {
  family: {
    consent: {
      title: "Protegemos quem você ama",
      subtitle: "Para garantir a segurança, precisamos da sua autorização"
    },
    studentInfo: {
      title: "Quem vai aprender?",
      subtitle: "Vamos conhecer o estudante",
      namePlaceholder: "Como ele(a) gosta de ser chamado(a)?",
      agePlaceholder: "Quantos anos tem?"
    },
    goal: {
      title: "Qual é o sonho?",
      subtitle: "Isso nos ajuda a encontrar o educador ideal"
    },
    avatar: {
      title: "Escolha sua cara!",
      subtitle: "O avatar aparece no perfil do estudante"
    }
  },
  teacher: {
    checklist: {
      title: "Sua jornada começa aqui",
      subtitle: "Complete estas etapas para brilhar na plataforma"
    }
  },
  student: {
    missions: {
      title: "Suas Missões",
      completionTitle: "Explorador Certificado!"
    }
  },
  shared: {
    progress: {
      almostThere: "Quase lá!",
      lastStep: "Última etapa!",
      completed: "Concluído!"
    }
  }
} as const;

// Type exports for consumers
export type ErrorMessageKey = keyof typeof ERROR_MESSAGES;
export type SuccessMessageKey = keyof typeof SUCCESS_MESSAGES;
export type EmptyStateKey = keyof typeof EMPTY_STATES;
export type VideoMessageKey = keyof typeof VIDEO_MESSAGES;
export type OnboardingMessageKey = keyof typeof ONBOARDING_MESSAGES;
